import React, { useState } from 'react';
import { ArtworkImageRecord } from '../../../domain/inventory/types/product-types';

interface LiveCardPreviewProps {
  title: string;
  artist: string;
  medium: string;
  yearCreated: number;
  description: string;
  priceFormatted: string;
  images: ArtworkImageRecord[];
}

export const LiveCardPreview: React.FC<LiveCardPreviewProps> = ({
  title,
  artist,
  medium,
  yearCreated,
  description,
  priceFormatted,
  images,
}) => {
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgError, setImgError] = useState(false);

  const primaryImage = images.find((img) => img.isPrimary) || images[0];
  const fitMode = primaryImage?.fitMode || 'contain';

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between">
        <label className="text-xs font-semibold text-textMuted">Live Card Preview</label>
        <span className="text-[10px] text-sky-400 font-mono">
          fitMode: {fitMode} | {primaryImage?.role || 'primary'}
        </span>
      </div>

      <div className="w-full max-w-[320px] mx-auto bg-[#151d2a] border border-[#1e293b] rounded-xl overflow-hidden shadow-lg flex flex-col pointer-events-none select-none">
        {/* Viewport */}
        <div className="relative w-full pt-[75%] bg-[#090d16]">
          {!imgLoaded && !imgError && primaryImage?.url && (
            <div className="absolute inset-0 flex items-center justify-center bg-[#1e293b] text-[#64748b] text-[11px]">
              Loading preview...
            </div>
          )}
          {imgError || !primaryImage?.url ? (
            <div className="absolute inset-0 flex items-center justify-center bg-[#0f172a] text-[#ef4444] text-[11px]">
              {primaryImage?.url ? 'Image Unavailable' : 'No Image URL'}
            </div>
          ) : (
            <img
              src={primaryImage.url}
              alt={title || 'Preview'}
              onLoad={() => setImgLoaded(true)}
              onError={() => setImgError(true)}
              className="absolute inset-0 w-full h-full transition-opacity duration-300"
              style={{
                objectFit: fitMode === 'cover' ? 'cover' : 'contain',
                opacity: imgLoaded ? 1 : 0,
              }}
            />
          )}

          {images.length > 1 && (
            <div className="absolute bottom-2 left-2 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-slate-900/80 text-white border border-white/15 backdrop-blur-xs flex items-center gap-1">
              📷 1/{images.length}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-3.5 flex flex-col flex-1">
          <div className="text-sm font-bold text-white mb-0.5 truncate">{title || 'Untitled Artwork'}</div>
          <div className="text-xs text-sky-400 font-medium mb-1.5 truncate">{artist || 'Unknown Artist'}</div>
          <div className="text-[11px] text-slate-400 mb-2 truncate">
            {medium || 'Medium'} ({yearCreated || 'Year'})
          </div>

          <div
            className="text-[11px] text-slate-400 line-clamp-2 mb-2 leading-tight"
            style={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {description.trim() || 'No description provided.'}
          </div>

          <div className="mt-auto pt-2 border-t border-[#1e293b] flex items-center justify-between text-xs">
            <span className="font-bold text-emerald-400">{priceFormatted}</span>
            <span className="text-[10px] text-slate-500">Preview Mode</span>
          </div>
        </div>
      </div>
    </div>
  );
};
